// =========================================================================
// JEDNOTNÝ ZDROJ TEXTŮ VÁZANÝCH NA METRIKU
// -------------------------------------------------------------------------
// Tatáž třída chyby vznikla šestkrát: text napsaný pro jednu metriku
// (typicky INP — interakce, reklama, výkon zařízení) se dosadil k jiné
// (typicky TTFB, které končí dřív, než na klientovi cokoli běží).
//
// Pravidlo: KAŽDÉ místo v generátoru, které popisuje mechanismus metriky
// nebo důvod rozdílu mezi lab a field hodnotou, čte z této mapy. Žádné
// místo si text neskládá samo.
//
// Pojistka: chybí-li kombinace metrika × situace, vrací se PRÁZDNO a
// kombinace se zaloguje. Prázdná věta je lepší než věta o interakcích
// u TTFB.
//
// Mechanismy (co smí a nesmí text obsahovat):
//   TTFB — čistě serverová strana (zátěž serveru, cache hit/miss,
//          geografie, kvalita sítě). NIKDY interakce, reklama, výkon
//          zařízení.
//   CLS  — posuny rozložení (viewporty, reklamní sloty s proměnlivou
//          výškou, pozdě doskakující obsah). NIKDY skripty či interakce
//          jako příčina.
//   INP  — odezva na interakci (interakce, souběžná reklama, pomalejší
//          zařízení) — stávající znění je věcně správné.
//   LCP  — načtení největšího prvku (síť, zařízení, cache) — stávající
//          znění je věcně správné.
// =========================================================================

/**
 * Situace:
 *  - singleLabRun        popisek karty „laboratorní test" u jediného běhu
 *  - labDivergence       lab strana divergenční poznámky (field vs. lab)
 *  - notReproducedReason celá věta „lab nereprodukoval" s důvodem
 *                        specifickým pro metriku (params: mUp, fieldTxt, labTxt)
 *  - reproduceConditions podmínky, za kterých se problém reprodukuje
 *                        (vsazuje se do vět „Reprodukovat problém …")
 */
const METRIC_COPY = {
  inp: {
    singleLabRun: {
      cs: 'Jedna automatizovaná interakce v laboratoři. Field p75 shrnuje interakce skutečných návštěvníků za jiné období a podmínky; samotný rozdíl nedokazuje kolize s načítáním ani jejich příčinu.',
      en: 'Single automated interaction under lab conditions. Field p75 summarizes real visitors’ interactions over a different period and conditions; the gap alone does not prove load collisions or their cause.'
    },
    labDivergence: {
      cs: 'laboratorní test je jedna automatizovaná interakce v laboratorních podmínkách (bez zátěže a rušivých vlivů reálného zařízení)',
      en: 'the lab test is a single automated interaction under lab conditions (without the load and noise of a real device)'
    },
    notReproducedReason: {
      cs: 'příčina rozdílu není určena; souběžná reklama, více interakcí či pomalejší zařízení jsou hypotézy k ověření, nikoli závěr z tohoto běhu',
      en: 'the reason for the gap is unknown; concurrent ads, multiple interactions or slower devices are hypotheses to test, not conclusions from this run'
    },
    reproduceConditions: {
      cs: 'za podmínek bližších reálným uživatelům (mobilní emulace, CPU throttling, udělený souhlas, souběžná reklama, více interakcí)',
      en: 'under conditions closer to real users (mobile emulation, CPU throttling, consent granted, concurrent ads, multiple interactions)'
    },
    fieldContextTail: {
      cs: 'včetně pomalejších telefonů a delších návštěv s více interakcemi',
      en: 'including slower phones and longer sessions with more interactions'
    },
    diagnosticProtocol: {
      cs: 'Podmínky měření: mobilní emulace, CPU throttling 4×, udělený souhlas (cookies přijaty), aktivní reklama, ≥ 10 interakcí na hlavních prvcích stránky (menu, vyhledávání, odkazy článků). U každé interakce zaznamenat: cíl interakce, INP a rozpad na zpoždění vstupu / zpracování / vykreslení. Rozhodnutí podle dominantní fáze: zpoždění vstupu → profilovat dlouhé úlohy a third-party skripty běžící při interakci; zpracování → profilovat handlery cílového prvku; vykreslení → prověřit velikost DOM a rozsah překreslení.',
      en: 'Measurement conditions: mobile emulation, 4\u00d7 CPU throttling, consent granted, ads active, \u2265 10 interactions on the page\u2019s main elements (menu, search, article links). For each interaction record: the interaction target, INP and the breakdown into input delay / processing / presentation delay. Decide by the dominant phase: input delay \u2192 profile long tasks and third-party scripts running at interaction time; processing \u2192 profile the target element\u2019s handlers; presentation \u2192 check DOM size and repaint scope.'
    }
  },
  ttfb: {
    singleLabRun: {
      cs: 'Jedno automatizované načtení v laboratoři (čas do prvního bajtu — měří se dřív, než na klientovi cokoli běží). Lab měří jeden požadavek z jednoho místa a sítě; field p75 zahrnuje tisíce návštěv z různých sítí, lokalit a zátěže serveru — proto se může lišit a je reprezentativnější.',
      en: 'Single automated page load under lab conditions (time to first byte — measured before anything runs on the client). Lab measures one request from one location and network; field p75 spans thousands of visits across networks, locations and server load — that is why it can differ and is more representative.'
    },
    labDivergence: {
      cs: 'laboratorní test je jedno automatizované načtení v laboratorních podmínkách (čas do prvního bajtu, měřený před jakýmkoli během na klientovi, z jednoho místa a sítě)',
      en: 'the lab test is a single automated page load under lab conditions (time to first byte, measured before anything runs on the client, from one location and network)'
    },
    notReproducedReason: {
      cs: 'příčina rozdílu není určena; zátěž serveru, cache, geografie a kvalita sítě jsou možné podmínky k ověření',
      en: 'the reason for the gap is unknown; server load, cache, geography and network quality are possible conditions to test'
    },
    reproduceConditions: {
      cs: 'za podmínek bližších reálným návštěvám (měření v různých denních hodinách, cache miss i hit, z geografie návštěvníků a při běžné kvalitě jejich sítě)',
      en: 'under conditions closer to real visits (measuring at different times of day, cache miss and hit, from visitors\u2019 geography and typical network quality)'
    },
    fieldContextTail: {
      cs: 'včetně vzdálenějších lokalit, pomalejších sítí a špiček zátěže serveru',
      en: 'including more distant locations, slower networks and server load peaks'
    },
    diagnosticProtocol: {
      cs: 'Podmínky měření: opakované požadavky v různých denních hodinách (špička i mimo ni), cache miss i hit, z geografie návštěvníků a při běžné kvalitě jejich sítě; ≥ 5 běhů na stránkách, kde field TTFB vychází nejhůř. U každého požadavku zaznamenat TTFB a serverové fáze (Server-Timing, čas aplikace vs. čas cache/CDN, je-li k dispozici). Implementační ticket vzniká až po reprodukci.',
      en: 'Measurement conditions: repeated requests at different times of day (peak and off-peak), cache miss and hit, from visitors\u2019 geography and typical network quality; \u2265 5 runs on the pages where field TTFB is worst. For each request record TTFB and server-side phases (Server-Timing, application time vs. cache/CDN time when available). An implementation ticket follows only after reproduction.'
    }
  },
  lcp: {
    singleLabRun: {
      cs: 'Jedno automatizované načtení v laboratoři. Lab měří jedno načtení z jednoho místa a sítě; field p75 zahrnuje tisíce návštěv z různých zařízení a sítí — proto se může lišit a je reprezentativnější.',
      en: 'Single automated page load under lab conditions. Lab measures one load from one location and network; field p75 spans thousands of visits across devices and networks — that is why it can differ and is more representative.'
    },
    labDivergence: {
      cs: 'laboratorní test je jedno automatizované načtení v laboratorních podmínkách (z jednoho místa a sítě)',
      en: 'the lab test is a single automated page load under lab conditions (from one location and network)'
    },
    notReproducedReason: {
      cs: 'příčina rozdílu není určena; pomalejší sítě a zařízení či studená cache jsou hypotézy k ověření',
      en: 'the reason for the gap is unknown; slower networks or devices and cold cache are hypotheses to test'
    },
    reproduceConditions: {
      cs: 'za podmínek bližších reálným uživatelům (pomalejší síť a zařízení, studená cache při první návštěvě)',
      en: 'under conditions closer to real users (slower network and device, cold cache on first visit)'
    },
    fieldContextTail: {
      cs: 'včetně pomalejších sítí a zařízení a prvních návštěv bez cache',
      en: 'including slower networks and devices and first visits without cache'
    },
    diagnosticProtocol: {
      cs: 'Podmínky měření: pomalejší síť a zařízení (mobilní emulace, síťový throttling), studená cache při první návštěvě; opakované běhy (≥ 5) na stránkách, kde field LCP vychází nejhůř. U každého běhu zaznamenat LCP prvek a dominantní fázi (TTFB / zpoždění načtení / načtení zdroje / zpoždění vykreslení). Implementační ticket vzniká až po reprodukci.',
      en: 'Measurement conditions: slower network and device (mobile emulation, network throttling), cold cache on first visit; repeated runs (\u2265 5) on the pages where field LCP is worst. For each run record the LCP element and the dominant phase (TTFB / load delay / resource load / render delay). An implementation ticket follows only after reproduction.'
    }
  },
  cls: {
    singleLabRun: {
      cs: 'Jedno automatizované načtení v laboratoři. Posuny rozložení závisí na obsahu a velikosti viewportu (reklamní sloty s proměnlivou výškou, pozdě doskakující obsah); field p75 zahrnuje tisíce návštěv s různými viewporty a obsahem — proto se může lišit a je reprezentativnější.',
      en: 'Single automated page load under lab conditions. Layout shifts depend on content and viewport size (variable-height ad slots, late-arriving content); field p75 spans thousands of visits with different viewports and content — that is why it can differ and is more representative.'
    },
    labDivergence: {
      cs: 'laboratorní test je jedno automatizované načtení v laboratorních podmínkách (jeden viewport a jedna varianta obsahu)',
      en: 'the lab test is a single automated page load under lab conditions (one viewport and one content variant)'
    },
    notReproducedReason: {
      cs: 'příčina rozdílu není určena; jiné viewporty, proměnlivé reklamní sloty či pozdní obsah jsou hypotézy k ověření',
      en: 'the reason for the gap is unknown; other viewports, variable-height ad slots or late content are hypotheses to test'
    },
    reproduceConditions: {
      cs: 'za podmínek bližších reálným uživatelům (různé velikosti viewportu, aktivní reklamní sloty, pomalejší síť s pozdě doskakujícím obsahem)',
      en: 'under conditions closer to real users (different viewport sizes, active ad slots, slower network with late-arriving content)'
    },
    fieldContextTail: {
      cs: 'včetně jiných velikostí viewportu a variant obsahu (reklama, personalizace)',
      en: 'including other viewport sizes and content variants (ads, personalization)'
    },
    diagnosticProtocol: {
      cs: 'Podmínky měření: různé velikosti viewportu (mobil i desktop), aktivní reklamní sloty, pomalejší síť s pozdě doskakujícím obsahem; opakované běhy (≥ 5) na stránkách, kde field CLS vychází nejhůř. U každého běhu zaznamenat posunutý prvek a co posun spustilo (doskočivší obsah nad ním). Implementační ticket vzniká až po reprodukci.',
      en: 'Measurement conditions: different viewport sizes (mobile and desktop), active ad slots, slower network with late-arriving content; repeated runs (\u2265 5) on the pages where field CLS is worst. For each run record the shifted element and what triggered the shift (content arriving above it). An implementation ticket follows only after reproduction.'
    }
  }
};

export const METRIC_COPY_METRICS = Object.keys(METRIC_COPY);
export const METRIC_COPY_SITUATIONS = ['singleLabRun', 'labDivergence', 'notReproducedReason', 'reproduceConditions', 'fieldContextTail', 'diagnosticProtocol'];

/**
 * Jediný přístupový bod. Chybí-li kombinace, vrací '' a loguje — nikdy
 * negeneruje obecný text.
 */
export const metricCopy = (metric, situation, lang = 'cs') => {
  const m = String(metric || '').toLowerCase();
  const entry = METRIC_COPY[m] && METRIC_COPY[m][situation];
  if (!entry) {
    // eslint-disable-next-line no-console
    console.warn(`[metricCopy] Missing copy for metric="${m}" situation="${situation}" — returning empty string.`);
    return '';
  }
  return entry[lang === 'cs' ? 'cs' : 'en'] || '';
};
